import axiosInstance from "../../shared/api/axiosInstance";
import { trackInfo } from "../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents } from "../../shared/services/appInsightsConstants";

export interface InviteValidationResponse {
  data: {
    success: boolean;
    response: {
      isValid: boolean;
      reason:
        | "used"
        | "expired"
        | "not_found"
        | "invalid"
        | "revoked"
        | "ok"
        | string; // extendable
      emailMasked: string | null;
      expiresAt: string | null;
    };
    exception: string | null;
    message: string;
  };
  status: number;
  statusText: string;
}

export interface AcceptInviteRequest {
  inviteId: string;
  password: string;
  token: string;
  // add other fields as needed
}

export interface AcceptInviteResponse {
  data: {
    success: boolean;
    response: {
      isCompleted: boolean;
      reason: "used_account_issue" | "not_found" | string; // extendable
    };
    exception: string | null;
    message: string;
  };
  status: number;
  statusText: string;
}

/**
 * Validate an invitation link
 * @param guid - The invitation GUID from the URL
 * @param token - The token parameter from the URL (t=...)
 */
export const validateInvite = async (
  guid: string,
  token: string
): Promise<InviteValidationResponse> => {
  // Track API call (minimal logging for API layer)
  trackInfo("Invite validation API call", {
    component: AppInsightsComponents.INVITE_API,
    action: AppInsightsActions.API_REQUEST,
    endpoint: "validate-invite"
  });

  const response = await axiosInstance.get(`/invites/${guid}`, {
    params: { t: token },
  });
  return response;
};

/**
 * Accept an invitation and set up the user account
 * @param inviteData - The invitation acceptance data including password
 */
export const acceptInvite = async (
  inviteData: AcceptInviteRequest
): Promise<AcceptInviteResponse> => {
  // Track API call (minimal logging for API layer)
  trackInfo("Invite acceptance API call", {
    component: AppInsightsComponents.INVITE_API,
    action: AppInsightsActions.API_REQUEST,
    endpoint: "accept-invite"
  });

  const response = await axiosInstance.post(
    `/invites/${inviteData.inviteId}/accept`,
    inviteData
  );
  return response;
};
